import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UserSessionData {
  userId: string | null;
  projectId: string | null;
  projectName: string | null;
  assignedState: string | null;
  roles: string[];
  locations: Array<{ location_id: string; locations?: { name: string; state?: string | null } | null }>;
  locationIds: string[];
  userState: string | null;
  role: string | null;
  isAdmin: boolean;
  isMasterAdmin: boolean;
  isCenterManager: boolean;
  isStatePartner: boolean;
  isGrowthPartner: boolean;
  // Profile fields — added so RoleContext / ProfileCompletionWizard /
  // page tour and project-name hooks don't have to re-fetch `profiles`.
  fullName: string | null;
  email: string | null;
  userType: string | null;
  avatarUrl: string | null;
  phone: string | null;
  profileCompleted: boolean | null;
  tourCompleted: boolean | null;
}

const EMPTY: UserSessionData = {
  userId: null,
  projectId: null,
  projectName: null,
  assignedState: null,
  roles: [],
  locations: [],
  locationIds: [],
  userState: null,
  role: null,
  isAdmin: false,
  isMasterAdmin: false,
  isCenterManager: false,
  isStatePartner: false,
  isGrowthPartner: false,
  fullName: null,
  email: null,
  userType: null,
  avatarUrl: null,
  phone: null,
  profileCompleted: null,
  tourCompleted: null,
};

/**
 * Shared query key + config for the user-session query. Exported so the
 * producer (LandingPage priming via setQueryData) and the consumer
 * (useUserSession) cannot diverge on key or freshness settings.
 */
export const USER_SESSION_QUERY_KEY = ["user-session"] as const;
export const USER_SESSION_STALE_TIME = 2 * 60 * 1000;

/**
 * Single combined fetch for the auth-derived "shell" data that almost every
 * page needs: user id, project_id, assigned_state, roles, locations, and
 * the basic profile fields used by RoleContext / ProfileCompletionWizard
 * / page-tour / project-name.
 *
 * Previously each shell component made its own round-trip (~30 calls on
 * every page enter). All consumers now delegate to this single query, so
 * React Query dedupes the work to one batched fetch per session.
 */
export function useUserSession() {
  return useQuery({
    queryKey: USER_SESSION_QUERY_KEY,
    queryFn: async (): Promise<UserSessionData> => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;
      if (!userId) return EMPTY;

      const { data: ctxRaw, error: ctxError } = await supabase.rpc("get_my_context" as any);

      // Do NOT swallow RPC failures — if we do, `ctxRaw` becomes null,
      // `roles` becomes [], and RoleContext mis-resolves an authenticated
      // user to `no_role` (the "Account setup incomplete" screen) even
      // though they have valid roles. Throw so React Query retries.
      if (ctxError) {
        throw new Error(`get_my_context failed: ${ctxError.message}`);
      }

      const ctx = (ctxRaw ?? {}) as any;

      // Signed-out signal from the RPC — treat identically to the pre-RPC
      // signed-out path so downstream consumers see EMPTY.
      if (ctx?.error === "not_authenticated") return EMPTY;

      // Defensive: if the RPC returned a shape without a roles array while
      // we DO have an authenticated userId, treat that as a transient
      // failure and throw so React Query retries instead of caching a
      // bogus "no roles" snapshot for the whole staleTime window.
      if (!Array.isArray(ctx.roles)) {
        throw new Error("get_my_context returned no roles array for authenticated user");
      }

      const profile = (ctx.profile ?? null) as any;
      const roles = Array.isArray(ctx.roles) ? (ctx.roles as string[]) : [];
      const isMasterAdmin = roles.includes("master_admin");
      const isAdmin = roles.includes("admin") || isMasterAdmin;
      const isCenterManager = roles.includes("center_manager");
      const isStatePartner = roles.includes("state_partner");
      const isGrowthPartner = roles.includes("growth_partner");

      // Rebuild the nested `{ location_id, locations: { name, state } }`
      // shape that consumers (and TypeScript) still expect.
      const rawLocations = Array.isArray(ctx.locations) ? ctx.locations : [];
      const locations = rawLocations.map((l: any) => ({
        location_id: l.location_id as string,
        locations:
          l && (l.name !== undefined || l.state !== undefined)
            ? { name: (l.name ?? "") as string, state: (l.state ?? null) as string | null }
            : null,
      })) as UserSessionData["locations"];
      const locationIds = locations.map((l) => l.location_id);

      const locationState =
        ((ctx.location_state as string | null) ?? null) ||
        (locations[0]?.locations as any)?.state ||
        null;

      const assignedState = (profile?.assigned_state as string | null) ?? null;

      const userState =
        (isStatePartner ? assignedState : null) || locationState;

      return {
        userId,
        projectId: (profile?.project_id as string | null) ?? null,
        projectName: (ctx.project_name as string | null) ?? null,
        assignedState,
        roles,
        locations,
        locationIds,
        userState,
        role: isMasterAdmin ? "master_admin" : roles[0] || null,
        isAdmin,
        isMasterAdmin,
        isCenterManager,
        isStatePartner,
        isGrowthPartner,
        fullName: (profile?.full_name as string | null) ?? null,
        email: (profile?.email as string | null) ?? session?.user?.email ?? null,
        userType: (profile?.user_type as string | null) ?? null,
        avatarUrl: (profile?.avatar_url as string | null) ?? null,
        phone: (profile?.phone as string | null) ?? null,
        profileCompleted: (profile?.profile_completed as boolean | null) ?? null,
        tourCompleted: (profile?.tour_completed as boolean | null) ?? null,
      };
    },
    staleTime: USER_SESSION_STALE_TIME,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}
